import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Building2 } from "lucide-react";
import { projectNodePaths } from "lib/utils/projectNodePaths";
import { getProjectNodeQr } from "lib/api/getFacilityQr";
import FacilityQrHistory from "pages/qr/FacilityQrHistory";
import { buildProjectRoadmap, buildProjectRoadmapSearchParams, normalizeRoadmapLevels, PROJECT_ROADMAP_QUERY_KEYS, PROJECT_ROADMAP_UNASSIGNED_VALUE } from "../ProjectRoadmapPanel";

const LEVELS = normalizeRoadmapLevels(["buildingName", "floor", "location", "facilityName", "partName"]);
const SUMMARY_LEVELS = LEVELS;

function NodeTimeseriesSummary({ nodeId }) {
  const [result, setResult] = useState({ state: "loading" });
  useEffect(() => {
    if (!nodeId) return;
    const controller = new AbortController();
    setResult({ state: "loading" });
    getProjectNodeQr(nodeId, controller.signal, true).then(response => {
      if (!controller.signal.aborted) setResult(response.success ? { ...response.data, state: "ready" } : { state: "error" });
    }).catch(() => { if (!controller.signal.aborted) setResult({ state: "error" }); });
    return () => controller.abort();
  }, [nodeId]);
  return <section className="treeBranchSummary treeBranchSummary--timeseries" aria-label="부품 시계열 추이">
    {!nodeId ? <p>건물부터 부품까지 지정된 경로를 선택하세요.</p> : result.state === "loading" ? <p role="status">추이를 불러오는 중…</p>
      : result.state === "error" ? <p role="alert">추이를 불러오지 못했습니다.</p>
        : result.can_analyze ? <FacilityQrHistory files={result.files || []} compact />
          : <p>건물부터 부품까지 모든 경로가 등록되어야 분석할 수 있습니다.</p>}
  </section>;
}

export function ProjectTreeBranchSummary({ projectId, nodes, files, listScope, loading, error }) {
  const tree = useMemo(() => buildProjectRoadmap({ project_id: projectId, nodes, files }, SUMMARY_LEVELS), [projectId, nodes, files]);
  const depth = LEVELS.reduce((last, level, index) => listScope[PROJECT_ROADMAP_QUERY_KEYS[level.selectionKey]] ? index : last, -1);
  const selected = [];
  const collect = (items, index = 0, ancestors = {}) => {
    const level = SUMMARY_LEVELS[index];
    items.forEach(node => {
      const value = node.value ?? PROJECT_ROADMAP_UNASSIGNED_VALUE;
      const selectedValue = listScope[PROJECT_ROADMAP_QUERY_KEYS[level.selectionKey]];
      if (selectedValue && selectedValue !== value) return;
      const selection = { ...ancestors, [level.selectionKey]: value };
      if (index === depth) selected.push({ node, selection });
      else if (index < depth) collect(node[level.childrenKey] || [], index + 1, selection);
    });
  };
  collect(tree.buildings);
  if (depth === 4) {
    const matches = projectNodePaths(nodes).filter(path => LEVELS.every(level => path[level.field] && path[level.field] === listScope[PROJECT_ROADMAP_QUERY_KEYS[level.selectionKey]]));
    return <NodeTimeseriesSummary nodeId={matches.length === 1 ? matches[0].id : null} />;
  }
  if (depth === 2 || depth === 3) {
    const children = selected.flatMap(({ node, selection }) => (node[LEVELS[depth].childrenKey] || []).map(child => ({ child, selection: {
      ...selection, [LEVELS[depth + 1].selectionKey]: child.value ?? PROJECT_ROADMAP_UNASSIGNED_VALUE,
    } })));
    const Icon = LEVELS[depth + 1].icon;
    return <nav className="treeBranchSummary treeBranchSummary--list" aria-label={depth === 2 ? "등록된 설비 목록" : "등록된 부품 목록"}>
      {loading ? <p role="status">목록을 불러오는 중…</p> : error ? <p role="alert">목록을 불러오지 못했습니다.</p> : children.length ? <ul>
        {children.map(({ child, selection }) => <li key={JSON.stringify(selection)}><Link className="treeBranchSummary__node"
          to={`/project/${projectId}?${buildProjectRoadmapSearchParams(new URLSearchParams(listScope), selection)}`}>
          <Icon size={14} aria-hidden="true" /><span>{child.value || "미지정"}</span>
        </Link></li>)}
      </ul> : <p>등록된 {depth === 2 ? "설비" : "부품"}가 없습니다.</p>}
    </nav>;
  }
  const branch = (node, index, selection) => {
    const level = SUMMARY_LEVELS[index], Icon = level.icon;
    const children = node[level.childrenKey] || [];
    const params = buildProjectRoadmapSearchParams(new URLSearchParams(listScope), selection);
    return <li key={JSON.stringify(selection)}>
      <Link className="treeBranchSummary__node" to={`/project/${projectId}?${params.toString()}`} title={`${level.defaultLabel}: ${node.value ?? "미지정"}`}>
        <Icon size={14} aria-hidden="true" /><span>{node.value ?? "미지정"}</span>
      </Link>
      {children.length > 0 && <ul>{children.map(child => branch(child, index + 1, {
        ...selection, [SUMMARY_LEVELS[index + 1].selectionKey]: child.value ?? PROJECT_ROADMAP_UNASSIGNED_VALUE,
      }))}</ul>}
    </li>;
  };
  return <nav className="treeBranchSummary" aria-label="선택한 항목의 하위 경로" tabIndex={0}>
    {loading ? <p role="status">하위 경로를 불러오는 중…</p> : error ? <p role="status">하위 경로를 불러오지 못했습니다.</p> : selected.length
      ? <ul className="treeBranchSummary__canvas">{selected.map(({ node, selection }) => branch(node, depth, selection))}</ul>
      : <p>등록된 하위 경로가 없습니다.</p>}
  </nav>;
}

export default function ProjectTreeOverview({ projectId, projectName, nodes, files, listScope, loading, error }) {
  const tree = useMemo(() => buildProjectRoadmap({ project_id: projectId, nodes, files }, LEVELS), [projectId, nodes, files]);
  const branches = (items, depth = 0, ancestors = {}) => <ul>
    {items.map(node => {
      const level = LEVELS[depth], Icon = level.icon;
      const selection = { ...ancestors, [level.selectionKey]: node.value ?? PROJECT_ROADMAP_UNASSIGNED_VALUE };
      const children = node[level.childrenKey] || [];
      const params = buildProjectRoadmapSearchParams(new URLSearchParams(listScope), selection);
      return <li key={node.value ?? PROJECT_ROADMAP_UNASSIGNED_VALUE}>
        <div className="treeOverview__nodeWrap">
        <Link to={`/project/${projectId}?${params.toString()}`} className="treeOverview__node">
          <Icon size={16} aria-hidden="true" /><span><small>{level.defaultLabel}</small><strong>{node.value ?? "미지정"}</strong></span>
        </Link>
        </div>
        {children.length > 0 && branches(children, depth + 1, selection)}
      </li>;
    })}
  </ul>;
  return <nav className="treeOverview" aria-label="프로젝트 전체 경로" tabIndex={0}>
    <div className="treeOverview__canvas">
    <div className="treeOverview__root"><Building2 size={18} aria-hidden="true" /><strong>{projectName || "프로젝트"}</strong></div>
    {loading ? <p role="status">경로를 불러오는 중…</p> : error ? <p role="status">일부 등록 경로를 불러오지 못했습니다.</p> : null}
    {tree.buildings.length ? branches(tree.buildings) : null}
    </div>
  </nav>;
}
